import React, { memo, useMemo } from 'react';
import ColorSwatch from './ColorSwatch';
import ColorSwatchStyles from './ColorSwatchStyles';
import { arrayGroupBy, generateCompare, arrayDedupe } from '../utils/array.util';
import classNames from 'classnames';
import { formatCurrency } from '../utils/formatters.util';
import { pipe } from '../utils/helper.util';
import Big from 'big.js';

const ColorSwatches = ({
  activeColor,
  colors,
  itemHandler,
  ignoreDisabledStyle = false,
  wrapperClassName = '',
  activeColorValueField = 'value',
  indicateSale = false,
  indicateClearance = true,
  indicateLimitedEdition = true,
  feeTotal = 0,
  showBaseTitle = true,
  showHeader = true,
  colorPickerClassName = '',
  swatchSize = 'md',
  swatchesAddendum = null,
  centered,
  hideActiveAvailability,
  labelSize = 'md',
  hideOOS = false,
}) => {
  const baseTitle = showBaseTitle
    ? window.location.pathname.toLowerCase().includes('the-get-out-and-go-series-tumbler')
      ? 'Activity'
      : 'Color'
    : null;

  // Group colors
  const sortedColorPriceSaleGroups = pipe(
    (arr) =>
      arrayGroupBy(arr, (c) => ({
        isSale: indicateSale && !!c.maxAdditionalSalePercent,
        isClearance: indicateClearance && !!c.maxAdditionalSalePercent && !!c.isClearance,
        isLimitedEdition: indicateLimitedEdition && !!c.isLimitedEdition,
        isNBCF: indicateLimitedEdition && c.isLimitedEdition && c.id === 340,
      })),
    (map) => Array.from(map.entries()),
    (arr) => arr.sort(colorGroupCompare),
    (arr) =>
      arr.map(([key, arr]) => [
        key,
        key.isSale || key.isClearance
          ? arr.sort(
              (a, b) => b.maxAdditionalSalePercent - a.maxAdditionalSalePercent || a.sortOrder - b.sortOrder
            )
          : arr,
      ])
  )(colors);

  return sortedColorPriceSaleGroups.map(([{ isSale, isClearance, isLimitedEdition, isNBCF }, elements]) => {
    if (hideOOS && !elements.find((e) => e.isInStock || e.value === activeColor?.value)) {
      return null;
    }

    const includesActive = !!activeColor && !!elements.find((e) => e.id === activeColor?.id);

    const rateRangeWithFee = includesActive
      ? { min: activeColor.rate + feeTotal, max: activeColor.rate + feeTotal }
      : (() => {
          const head = elements[0];
          const rateRange = elements.reduce(
            (acc, el) => ({ min: Math.min(acc.min, el?.rate ?? 0), max: Math.max(acc.max, el?.rate ?? 0) }),
            { min: head?.rate ?? 0, max: head?.rate ?? 0 }
          );

          return {
            min: Big(rateRange.min).plus(feeTotal).toNumber(),
            max: Big(rateRange.max).plus(feeTotal).toNumber(),
          };
        })();

    const keyPrefix =
      [isSale && 'sale', isClearance && 'clearance', isLimitedEdition && 'le', isNBCF && 'nbcf']
        .filter((p) => !!p)
        .join('-') || 'basic';

    return (
      <ColorSwatchesGroup
        key={`${keyPrefix}-${rateRangeWithFee?.min}-${rateRangeWithFee?.max}`}
        rateRange={rateRangeWithFee}
        colors={elements}
        selectedInTitle={!!elements.find((e) => e.id === activeColor?.id)}
        {...{
          activeColor,
          activeColorValueField,
          baseTitle,
          centered,
          colorPickerClassName,
          hideActiveAvailability,
          hideOOS,
          ignoreDisabledStyle,
          isClearance,
          isLimitedEdition,
          isNBCF,
          isSale,
          itemHandler,
          keyPrefix,
          labelSize,
          showHeader,
          swatchesAddendum,
          swatchSize,
          wrapperClassName,
        }}
      />
    );
  });
};

export default memo(ColorSwatches);

const ColorSwatchesGroup = ({
  keyPrefix,
  activeColor,
  colors,
  itemHandler,
  ignoreDisabledStyle,
  wrapperClassName,
  activeColorValueField,
  baseTitle,
  rateRange,
  isSale,
  isClearance,
  isLimitedEdition,
  isNBCF,
  selectedInTitle = true,
  rateInTitle,
  showHeader,
  colorPickerClassName = '',
  swatchSize = 'md',
  swatchesAddendum,
  centered = false,
  hideActiveAvailability = false,
  labelSize = 'md',
  hideOOS = false,
}) => {
  const shownColors = useMemo(
    () =>
      hideOOS ? colors.filter((color) => color.isInStock || color.value === activeColor?.value) : colors,
    [colors, hideOOS, activeColor]
  );

  return (
    <div className={classNames('product-colors tw:w-full', wrapperClassName)}>
      <div className={classNames('', colorPickerClassName)}>
        <ColorSwatchStyles colors={shownColors} />
        <div className={classNames('swatches tw:flex tw:flex-col', { 'tw:justify-center': centered })}>
          {showHeader && (
            <div
              className={classNames('tw:flex tw:items-center tw:gap-2', {
                'tw:mb-2': swatchSize === 'md',
                'tw:mb-1': swatchSize === 'sm',
              })}>
              <Title
                {...{
                  baseTitle,
                  rateRange,
                  isSale,
                  isClearance,
                  isLimitedEdition,
                  isNBCF,
                  rateInTitle,
                  labelSize,
                }}
              />
              <i
                className={classNames('tw:font-italic tw:font-bold tw:text-body', {
                  'tw:text-base': labelSize === 'md',
                  'tw:text-sm': labelSize === 'sm',
                })}>
                {selectedInTitle && (activeColor ? `${activeColor[activeColorValueField]}` : 'Select color')}
                {!ignoreDisabledStyle &&
                  !hideActiveAvailability &&
                  selectedInTitle &&
                  activeColor &&
                  (!activeColor.isInStock ? (
                    <span> - Out Of Stock</span>
                  ) : (
                    !activeColor.isAvailable && <span> - Unavailable</span>
                  ))}
              </i>
            </div>
          )}
          <div
            className={classNames('tw:flex tw:flex-wrap', { 'tw:justify-center': centered })}
            style={{ gap: 'var(--color-swatch-margin)' }}>
            {shownColors.map((swatchColor) => (
              <ColorSwatch
                key={swatchColor.id}
                itemHandler={itemHandler}
                color={swatchColor}
                isActive={activeColor?.id === swatchColor.id}
                ignoreDisabledStyle={ignoreDisabledStyle}
                size={swatchSize}
                activeColorValueField={activeColorValueField}
              />
            ))}
            {swatchesAddendum}
          </div>
          {isNBCF && (
            <div>
              <div className='tw:mt-4 tw:flex tw:flex-row'>
                <div className='tw:basis-auto'>
                  <img
                    src={require('/public/images/common/nbcf-ribbon-logo.svg')}
                    style={{
                      width: '2rem',
                      height: '2rem',
                    }}
                  />
                </div>
                <div className='tw:grow tw:pl-2 tw:text-sm'>
                  100% of gross retail sales from this limited edition collection will be donated to
                  <br />
                  National Breast Cancer Foundation.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Title = ({
  baseTitle,
  rateRange,
  rateInTitle,
  isSale,
  isClearance,
  isLimitedEdition,
  isNBCF,
  labelSize,
}) => {
  const rateRangeFormatted =
    rateRange &&
    rateInTitle &&
    arrayDedupe([rateRange.min, rateRange.max])
      .map((rate) => formatCurrency(rate))
      .join(' - ');

  const prefix = isNBCF
    ? 'Support the Cause'
    : isLimitedEdition
      ? 'Limited Edition'
      : isClearance
        ? 'Clearance'
        : isSale
          ? 'Sale'
          : '';

  const titleFormatted = [prefix, !isLimitedEdition && baseTitle, rateRangeFormatted]
    .filter((s) => !!s)
    .join(' ');

  return (
    <span
      className={classNames(
        'tw:font-normal',
        {
          'tw:text-onsale': isSale || isClearance,
          'tw:text-[#555555]': !isSale && !isClearance,
        },
        {
          'tw:text-base': labelSize === 'md',
          'tw:text-sm': labelSize === 'sm',
        }
      )}>
      {titleFormatted}
    </span>
  );
};

const colorGroupCompare = generateCompare(
  ([{ isNBCF }]) => !isNBCF,
  ([{ isLimitedEdition }]) => !isLimitedEdition,
  ([{ isClearance }]) => !isClearance,
  ([{ isSale }]) => !isSale,
  ([, elements]) => elements[0]?.rate
);
